import { Badge } from "@bcc/components/ui/badge";
import { Input } from "@bcc/components/ui/input";
import { X } from "lucide-react";
import { useCallback, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  ORDER_TAG_MAX_LENGTH,
  setOrderConfigurationRemoveTagButtonTestId,
  setOrderConfigurationTagsDraftInputTestId,
  setOrderConfigurationTagsInputTestId,
  setOrderConfigurationTagTestId,
} from "./order-configuration.utils";

interface OrderTagsInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

/**
 * Free-text tag editor for order-level tags. Tags are committed on Enter, on a
 * comma keystroke, or on blur; the most recent tag can be removed with
 * Backspace while the draft field is empty. Ports the legacy Beanz Connect
 * `TagInput` behaviour to the BCC component library.
 */
export function OrderTagsInput({ values, onChange, disabled = false }: OrderTagsInputProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");

  const commitDraft = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setDraft("");
  }, [draft, onChange, values]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        commitDraft();
        return;
      }
      if (event.key === "Backspace" && draft === "" && values.length > 0) {
        onChange(values.slice(0, -1));
      }
    },
    [commitDraft, draft, onChange, values]
  );

  // Remove by index so duplicate tag values each remove exactly one entry.
  const handleRemove = useCallback(
    (index: number) => {
      onChange(values.filter((_, valueIndex) => valueIndex !== index));
    },
    [onChange, values]
  );

  return (
    <div className="flex w-full flex-col gap-2" data-testid={setOrderConfigurationTagsInputTestId()}>
      <Input
        value={draft}
        disabled={disabled}
        maxLength={ORDER_TAG_MAX_LENGTH}
        placeholder={t("views.preferences.orderConfiguration.tagsPlaceholder")}
        testId={setOrderConfigurationTagsDraftInputTestId()}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commitDraft}
      />

      {values.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {values.map((tag, index) => (
            <Badge key={`${tag}-${index}`} variant="secondary" className="gap-1" testId={setOrderConfigurationTagTestId(tag)}>
              <span>{tag}</span>
              {!disabled && (
                <button
                  type="button"
                  aria-label={t("views.preferences.orderConfiguration.removeTag", { tag })}
                  className="rounded-sm hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  data-testid={setOrderConfigurationRemoveTagButtonTestId(tag)}
                  onClick={() => handleRemove(index)}
                >
                  <X className="size-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
